import type { ErrorInfo, HydrationOptions } from 'react-dom/client';
import {
	addErrorForHydrationStats,
	classifyAsHydrationError,
} from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import {
	extractOwnershipStack,
	getContainingPackageName,
	getOwningTeam,
} from '@jira/platform__ownership-marker/src/ui/extractors.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { getRouterState } from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';

import { captureException as reportToSentry } from '@atlassian/jira-capture-exception/src/index.tsx';

const location = 'react-hydrate-root';

export const hydrationOptions: HydrationOptions = {
	onRecoverableError: (untypedError, errorInfo) => {
		if (errorInfo.digest === 'ignore') {
			// the server has told us to ignore this error
			return;
		}

		const error = toTypedError(untypedError);
		const ownershipMetadata = extractOwnershipMetadata(errorInfo);
		const routeMetadata = getRouteMetadata();

		// When cleaning up, replace with @atlassian/jira-coinflip depending on the desired rate
		if (fg('jfp-magma-hydration-report-to-splunk')) {
			logToSplunk(error, errorInfo, { ...ownershipMetadata, ...routeMetadata });
		}
		// When cleaning up, replace with @atlassian/jira-coinflip depending on the desired rate
		if (fg('jfp-magma-hydration-report-to-sentry')) {
			// Not passing in routeMetadata here because it's already implicitly captured in Sentry as userLocation
			reportToSentry(location, error, { ...errorInfo, ...ownershipMetadata });
		}
		addErrorForHydrationStats(error, errorInfo);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.__HYDRATION_RECOVERABLE_ERRORS__ = window.__HYDRATION_RECOVERABLE_ERRORS__ ?? [];
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.__HYDRATION_RECOVERABLE_ERRORS__.push({
			error,
			errorInfo,
			ownershipMetadata,
			routeMetadata,
			hydrationErrorType: classifyAsHydrationError(error),
		});
	},
};

const toTypedError = (untypedError: unknown) => {
	if (untypedError instanceof Error) {
		return untypedError;
	}
	try {
		return new Error(`Unknown error occurred during hydration: ${JSON.stringify(untypedError)}`);
	} catch (e) {
		return new Error(String(untypedError));
	}
};

type OwnershipMetadata = { teamName?: string; packageName?: string; ownershipStack?: string };
const extractOwnershipMetadata = ({ componentStack }: ErrorInfo): OwnershipMetadata => {
	if (!componentStack) {
		return {};
	}

	const ownershipStack = extractOwnershipStack(componentStack);
	const result: OwnershipMetadata = {
		ownershipStack: JSON.stringify(ownershipStack),
	};

	const teamName = getOwningTeam(ownershipStack);
	if (teamName) {
		result.teamName = teamName;
	}
	const packageName = getContainingPackageName(ownershipStack);
	if (packageName) {
		result.packageName = packageName;
	}
	return result;
};

type RouteMetadata = { routeName?: string };
const getRouteMetadata = (): RouteMetadata => {
	const routerState = getRouterState();
	if (routerState?.route?.name) {
		return { routeName: routerState.route.name };
	}

	return {};
};

type Metadata = OwnershipMetadata & RouteMetadata;
const logToSplunk = (error: Error, errorInfo: ErrorInfo, metadata: Metadata) => {
	const { componentStack, digest } = errorInfo;
	const event: Record<string, string | undefined> = { digest, ...metadata };
	if (componentStack) {
		const lines = componentStack
			.trim()
			.split('\n')
			.map((line) => line.trim());
		if (lines.length > 25) {
			event.shortenedComponentStack = [...lines.slice(0, 20), '...', ...lines.slice(-5)].join('\n');
		} else {
			event.shortenedComponentStack = componentStack;
		}
	}

	log.safeErrorWithoutCustomerData(location, `onRecoverableError: ${error.message}`, event);
};
